// frontendRatiosCalculations.ts
import { AccountEntry, CategoryTemplate, Project } from '../../types';
import { compareMonthYear, getPeriodDays } from './calculations';
import { FrontendPLCalculationService } from './frontendPLCalculations';
import { FrontendBSCalculationService } from './frontendBSCalculations';
import { FrontendCFCalculationService } from './frontendCFCalculations';
import { FrontendNWCCalculationService } from './frontendNWCCalculations';

export interface RatioLineItem {
  name: string;
  type: 'ratio' | 'section_header';
  level: number;
  formula?: string;
  description?: string;
  account_mappings?: string[];
  values: (number | null)[];
  category?: string;
  format?: 'percentage' | 'decimal' | 'multiple' | 'days';
}

export interface RatiosData {
  periods: string[];
  line_items: RatioLineItem[];
  sections: any[];
}

export class FrontendRatiosCalculationService {
  static calculate(
    accounts: AccountEntry[],
    activeTemplate: CategoryTemplate,
    viewType: 'monthly' | 'quarterly' | 'yearly' = 'monthly',
    periodType?: string,
    selectedProject?: Project | null
  ): RatiosData {
    // console.log('[Ratios Frontend] Starting frontend ratio calculations');
    
    // Get base financial data from existing services
    const plData = FrontendPLCalculationService.buildPLData(accounts, activeTemplate, 'monthly', selectedProject);
    const bsData = FrontendBSCalculationService.buildBSData(accounts, activeTemplate, 'monthly', selectedProject);
    const cfData = FrontendCFCalculationService.buildCFData(accounts, activeTemplate, 'monthly', selectedProject);
    const nwcData = FrontendNWCCalculationService.calculate(accounts, activeTemplate, 'monthly');
    
    if (!plData || !bsData || !cfData || !nwcData) {
      console.warn('[Ratios Frontend] Missing base financial data');
      return { periods: [], line_items: [], sections: [] };
    }
    
    // Get periods based on view type
    const periods = this.getPeriods(plData.periods, viewType);
    // console.log('[Ratios Frontend] Calculated periods:', periods);
    
    // Build ratio line items
    const line_items = this.buildRatioLineItems(
      plData, 
      bsData, 
      cfData,
      nwcData, 
      periods, 
      viewType, 
      periodType || selectedProject?.period_type || 'monthly',
      activeTemplate,
      accounts
    );
    
    const sections = this.getRatioSections();
    
    // console.log('[Ratios Frontend] Generated ratios:', {
    //   periodsCount: periods.length,
    //   lineItemsCount: line_items.length,
    //   sectionsCount: sections.length
    // });
    
    return { periods, line_items, sections };
  }
  
  private static getPeriods(allPeriods: string[], viewType: 'monthly' | 'quarterly' | 'yearly'): string[] {
    const sortedPeriods = [...allPeriods].sort(compareMonthYear);
    
    if (viewType === 'monthly') {
      return sortedPeriods;
    }
    
    const mostRecentPeriod = sortedPeriods[sortedPeriods.length - 1];
    
    if (viewType === 'quarterly') {
      const quarterEndMonths = sortedPeriods.filter(period => {
        const monthStr = period.split('-')[0];
        return ['Mar', 'Jun', 'Sep', 'Dec'].includes(monthStr);
      });
      
      if (!quarterEndMonths.includes(mostRecentPeriod)) {
        quarterEndMonths.push(mostRecentPeriod);
      }
      
      return quarterEndMonths.sort(compareMonthYear);
    }
    
    if (viewType === 'yearly') {
      const yearEndMonths = sortedPeriods.filter(period => {
        const monthStr = period.split('-')[0];
        return monthStr === 'Dec';
      });
      
      if (!yearEndMonths.includes(mostRecentPeriod)) {
        yearEndMonths.push(mostRecentPeriod);
      }
      
      return yearEndMonths.sort(compareMonthYear);
    }
    
    return sortedPeriods;
  }
  
  private static buildRatioLineItems(
    plData: any,
    bsData: any,
    cfData: any,
    nwcData: any,
    periods: string[],
    viewType: 'monthly' | 'quarterly' | 'yearly',
    periodType: string,
    activeTemplate: CategoryTemplate,
    accounts: AccountEntry[]
  ): RatioLineItem[] {
    const line_items: RatioLineItem[] = [];
    
    // Define ratio calculations
    const ratioDefinitions = this.getRatioDefinitions();
    const sections = this.getRatioSections();
    
    sections.forEach(section => {
      // Add section header
      line_items.push({
        name: section.name,
        type: 'section_header',
        level: 1,
        values: periods.map(() => null),
        category: section.id
      });
      
      if (section.id === 'cash_conversion') {
        // Add NWC metrics using consistent calculations
        const nwcMetrics = this.buildNWCMetrics(nwcData, periods, viewType, periodType, activeTemplate, accounts);
        line_items.push(...nwcMetrics);
      } else {
        // Add regular financial ratios
        const sectionRatios = ratioDefinitions.filter(r => r.category === section.id);
        sectionRatios.forEach(ratio => {
          const values = periods.map(period => {
            const metrics = this.getMetrics(period, plData, bsData, cfData, nwcData, viewType, periodType);
            try {
              return ratio.calc(metrics);
            } catch (error) {
              console.warn(`[Ratios Frontend] Error calculating ${ratio.name} for period ${period}:`, error);
              return null;
            }
          });
          
          line_items.push({
            name: ratio.name,
            type: 'ratio',
            level: 1,
            formula: ratio.formula,
            description: ratio.description,
            account_mappings: ratio.account_mappings,
            values,
            category: section.id,
            format: ratio.format
          });
        });
      }
    });
    
    return line_items;
  }
  
  private static buildNWCMetrics(
    nwcData: any,
    periods: string[],
    viewType: 'monthly' | 'quarterly' | 'yearly',
    periodType: string,
    activeTemplate: CategoryTemplate,
    accounts: AccountEntry[]
  ): RatioLineItem[] {
    // console.log('[Ratios Frontend] Building NWC metrics using FrontendNWCCalculationService for code reuse');
    
    // Helper function to get values from the existing NWC service data
    const getNWCMetricValue = (metricName: string, period: string): number => {
      if (!nwcData || !nwcData.line_items) return 0;
      
      const metricItem = nwcData.line_items.find((item: any) => item.name === metricName);
      if (!metricItem || !metricItem.values) return 0;
      
      const periodIndex = nwcData.periods.indexOf(period);
      if (periodIndex === -1) return 0;
      
      return metricItem.values[periodIndex] || 0;
    };
    
    // Reuse calculations from FrontendNWCCalculationService instead of duplicating logic
    const nwcMetrics = [
      {
        name: 'Days Inventory Outstanding (DIO)',
        calc: (period: string) => getNWCMetricValue('Days Inventory Outstanding (DIO)', period),
        formula: 'Inventory ÷ COGS × Days',
        description: 'Average number of days inventory is held',
        account_mappings: ['Inventory', 'COGS']
      },
      {
        name: 'Days Sales Outstanding (DSO)',
        calc: (period: string) => getNWCMetricValue('Days Sales Outstanding (DSO)', period),
        formula: 'Accounts Receivable ÷ Revenue × Days',
        description: 'Average number of days to collect receivables',
        account_mappings: ['Accounts Receivable', 'Revenue']
      },
      {
        name: 'Days Payable Outstanding (DPO)',
        calc: (period: string) => getNWCMetricValue('Days Payable Outstanding (DPO)', period),
        formula: 'Accounts Payable ÷ COGS × Days',
        description: 'Average number of days to pay suppliers',
        account_mappings: ['Accounts Payable', 'COGS']
      },
      {
        name: 'Cash Conversion Cycle',
        calc: (period: string) => getNWCMetricValue('Cash Conversion Cycle', period),
        formula: 'DIO + DSO - DPO',
        description: 'Time to convert investments into cash flows',
        account_mappings: ['DIO', 'DSO', 'DPO']
      }
    ];
    
    return nwcMetrics.map(metric => {
      const values = periods.map(period => {
        try {
          return metric.calc(period);
        } catch (error) {
          console.warn(`[Ratios Frontend] Error calculating ${metric.name} for period ${period}:`, error);
          return null;
        }
      });
      
      return {
        name: metric.name,
        type: 'ratio' as const,
        level: 1,
        formula: metric.formula,
        description: metric.description,
        account_mappings: metric.account_mappings,
        values,
        category: 'cash_conversion',
        format: 'days' as const
      };
    });
  }
  
  // NWC calculations - consistent with UnifiedDetailedNetWorkingCapital.tsx
  private static calculateDIO(
    period: string,
    viewType: 'monthly' | 'quarterly' | 'yearly',
    periodType: string,
    activeTemplate: CategoryTemplate,
    accounts: AccountEntry[]
  ): number {
    const inventoryCategories = activeTemplate.categories
      .filter(cat => cat.nwcRole === 'inventory')
      .map(cat => cat.value);
    
    const cogsCategories = activeTemplate.categories
      .filter(cat => cat.type === 'cogs' || cat.type === 'contra-cogs')
      .map(cat => cat.value);
    
    if (viewType === 'monthly') {
      const inventory = this.getCategoryBalance(inventoryCategories, period, accounts, activeTemplate);
      const cogs = this.getCategoryBalance(cogsCategories, period, accounts, activeTemplate);
      const daysInMonth = this.getDaysInMonth(period);
      return cogs !== 0 ? (inventory / cogs) * daysInMonth : 0;
    } else {
      const monthsInPeriod = this.getMonthsInPeriod(period, viewType, accounts);
      const avgInventory = monthsInPeriod.reduce((sum, m) => 
        sum + this.getCategoryBalance(inventoryCategories, m, accounts, activeTemplate), 0) / monthsInPeriod.length;
      const periodCOGS = monthsInPeriod.reduce((sum, m) => 
        sum + this.getCategoryBalance(cogsCategories, m, accounts, activeTemplate), 0);
      const actualDays = this.getPeriodDaysForPeriod(period, periodType);
      return periodCOGS !== 0 ? (avgInventory / periodCOGS) * actualDays : 0;
    }
  }
  
  private static calculateDSO(
    period: string,
    viewType: 'monthly' | 'quarterly' | 'yearly',
    periodType: string,
    activeTemplate: CategoryTemplate,
    accounts: AccountEntry[]
  ): number {
    const arCategories = activeTemplate.categories
      .filter(cat => cat.nwcRole === 'accounts-receivable')
      .map(cat => cat.value);
    
    const revenueCategories = activeTemplate.categories
      .filter(cat => cat.type === 'revenue' || cat.type === 'contra-revenue')
      .map(cat => cat.value);
    
    if (viewType === 'monthly') {
      const ar = this.getCategoryBalance(arCategories, period, accounts, activeTemplate);
      const netRevenue = this.getCategoryBalance(revenueCategories, period, accounts, activeTemplate);
      const daysInMonth = this.getDaysInMonth(period);
      return netRevenue !== 0 ? (ar / netRevenue) * daysInMonth : 0;
    } else {
      const monthsInPeriod = this.getMonthsInPeriod(period, viewType, accounts);
      const avgAR = monthsInPeriod.reduce((sum, m) => 
        sum + this.getCategoryBalance(arCategories, m, accounts, activeTemplate), 0) / monthsInPeriod.length;
      const periodRevenue = monthsInPeriod.reduce((sum, m) => 
        sum + this.getCategoryBalance(revenueCategories, m, accounts, activeTemplate), 0);
      const actualDays = this.getPeriodDaysForPeriod(period, periodType);
      return periodRevenue !== 0 ? (avgAR / periodRevenue) * actualDays : 0;
    }
  }
  
  private static calculateDPO(
    period: string,
    viewType: 'monthly' | 'quarterly' | 'yearly',
    periodType: string,
    activeTemplate: CategoryTemplate,
    accounts: AccountEntry[]
  ): number {
    const apCategories = activeTemplate.categories
      .filter(cat => cat.nwcRole === 'accounts-payable')
      .map(cat => cat.value);
    
    const cogsCategories = activeTemplate.categories
      .filter(cat => cat.type === 'cogs' || cat.type === 'contra-cogs')
      .map(cat => cat.value);
    
    if (viewType === 'monthly') {
      const ap = this.getCategoryBalance(apCategories, period, accounts, activeTemplate);
      const cogs = this.getCategoryBalance(cogsCategories, period, accounts, activeTemplate);
      const daysInMonth = this.getDaysInMonth(period);
      return cogs !== 0 ? (ap / cogs) * daysInMonth : 0;
    } else {
      const monthsInPeriod = this.getMonthsInPeriod(period, viewType, accounts);
      const avgAP = monthsInPeriod.reduce((sum, m) => 
        sum + this.getCategoryBalance(apCategories, m, accounts, activeTemplate), 0) / monthsInPeriod.length;
      const periodCOGS = monthsInPeriod.reduce((sum, m) => 
        sum + this.getCategoryBalance(cogsCategories, m, accounts, activeTemplate), 0);
      const actualDays = this.getPeriodDaysForPeriod(period, periodType);
      return periodCOGS !== 0 ? (avgAP / periodCOGS) * actualDays : 0;
    }
  }
  
  // Helper methods
  private static getCategoryBalance(
    categories: string[],
    period: string,
    accounts: AccountEntry[],
    activeTemplate: CategoryTemplate
  ): number {
    return categories.reduce((total, category) => {
      const monthlyAccounts = accounts.filter(account => 
        account.category === category && account.month === period
      );
      
      const categoryType = activeTemplate.categories.find(cat => cat.value === category)?.type;
      const isAsset = ['current-asset', 'non-current-asset'].includes(categoryType || '');
      const isContraAsset = ['contra-current-asset', 'contra-non-current-asset'].includes(categoryType || '');
      const isRevenue = categoryType === 'revenue';
      const isContraRevenue = categoryType === 'contra-revenue';
      
      return total + monthlyAccounts.reduce((accTotal, account) => {
        if (isAsset || isContraAsset) {
          return accTotal + (account.debit - account.credit);
        } else if (isRevenue || isContraRevenue) {
          return accTotal + (account.credit - account.debit);
        } else {
          // Expenses and liabilities
          return accTotal + (account.debit - account.credit);
        }
      }, 0);
    }, 0);
  }
  
  private static getMonthsInPeriod(
    endMonth: string,
    viewType: 'monthly' | 'quarterly' | 'yearly',
    accounts: AccountEntry[]
  ): string[] {
    if (viewType === 'monthly') return [endMonth];
    
    const allMonths = [...new Set(accounts.map(a => a.month))].sort(compareMonthYear);
    const [endMonthStr, yearStr] = endMonth.split('-');
    const year = parseInt(yearStr);
    const monthIndex = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                       'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].indexOf(endMonthStr);
    
    let startMonthIndex;
    if (viewType === 'quarterly') {
      startMonthIndex = monthIndex - (monthIndex % 3);
    } else { // yearly
      startMonthIndex = 0;
    }
    
    return allMonths.filter(month => {
      const [monthStr, monthYearStr] = month.split('-');
      const monthYear = parseInt(monthYearStr);
      const monthIdx = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                       'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].indexOf(monthStr);
      
      if (monthYear !== year) return false;
      return monthIdx >= startMonthIndex && monthIdx <= monthIndex;
    });
  }
  
  private static getDaysInMonth(monthStr: string): number {
    const [month, year] = monthStr.split('-');
    const monthMap: { [key: string]: number } = {
      'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3, 'May': 4, 'Jun': 5,
      'Jul': 6, 'Aug': 7, 'Sep': 8, 'Oct': 9, 'Nov': 10, 'Dec': 11
    };
    const fullYear = 2000 + parseInt(year);
    return new Date(fullYear, monthMap[month] + 1, 0).getDate();
  }
  
  private static getPeriodDaysForPeriod(periodStr: string, periodType: string): number {
    if (periodType === 'monthly') {
      return this.getDaysInMonth(periodStr);
    } else if (periodType === 'quarterly') {
      const [monthStr, yearStr] = periodStr.split('-');
      const monthIndex = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].indexOf(monthStr);
      const quarterNum = Math.floor(monthIndex / 3) + 1;
      
      const quarterMonths = this.getMonthsInQuarter(quarterNum, 2000 + parseInt(yearStr));
      return quarterMonths.reduce((quarterDays, month) => {
        return quarterDays + new Date(2000 + parseInt(yearStr), month + 1, 0).getDate();
      }, 0);
    } else { // annual
      const [, yearStr] = periodStr.split('-');
      const year = 2000 + parseInt(yearStr);
      const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
      return isLeapYear ? 366 : 365;
    }
  }
  
  private static getMonthsInQuarter(quarterNum: number, year: number): number[] {
    switch (quarterNum) {
      case 1: return [0, 1, 2]; // Jan, Feb, Mar
      case 2: return [3, 4, 5]; // Apr, May, Jun
      case 3: return [6, 7, 8]; // Jul, Aug, Sep
      case 4: return [9, 10, 11]; // Oct, Nov, Dec
      default: return [];
    }
  }
  
  private static getMetrics(
    period: string,
    plData: any,
    bsData: any,
    cfData: any,
    nwcData: any,
    viewType: 'monthly' | 'quarterly' | 'yearly',
    periodType: string
  ) {
    // Get period index
    const periodIndex = plData.periods.indexOf(period);
    if (periodIndex === -1) {
      console.warn(`[Ratios Frontend] Period ${period} not found in data`);
      return {};
    }
    
    // Helper to get line item value
    const getLineItemValue = (data: any, itemName: string): number | null => {
      return FrontendRatiosCalculationService.getLineItemValue(data, itemName, periodIndex);
    };
    
    // Calculate annualization factor for P&L items
    const getAnnualizedValue = (value: number | null): number | null => {
      if (value === null) return null;
      
      let annualizationFactor: number;
      if (periodType === 'annual') {
        annualizationFactor = 1;
      } else if (periodType === 'quarterly') {
        annualizationFactor = viewType === 'quarterly' ? 4 : 1;
      } else { // monthly
        annualizationFactor = viewType === 'monthly' ? 12 : 1;
      }
      
      return value * annualizationFactor;
    };
    
    return {
      // Balance Sheet items (point-in-time, no annualization)
      currentAssets: getLineItemValue(bsData, 'Total Current Assets'),
      currentLiabilities: getLineItemValue(bsData, 'Total Current Liabilities'),
      totalAssets: getLineItemValue(bsData, 'Total Assets'),
      totalLiabilities: getLineItemValue(bsData, 'Total Liabilities'),
      totalEquity: getLineItemValue(bsData, 'Total Equity'),
      cash: getLineItemValue(bsData, 'Total Cash') || getLineItemValue(bsData, 'Cash'),
      shortTermInvestments: getLineItemValue(bsData, 'Short-Term Investments'),
      inventory: getLineItemValue(bsData, 'Inventory'),
      accountsReceivable: getLineItemValue(bsData, 'Total Trade Accounts Receivable'),
      accountsPayable: getLineItemValue(bsData, 'Trade Accounts Payable'),
      // Fix debt calculations - try to find actual debt line items, fallback to total liabilities
      shortTermDebt: getLineItemValue(bsData, 'Short-term Debt') || 
                     getLineItemValue(bsData, 'Total Short-term Debt') ||
                     getLineItemValue(bsData, 'Current Debt') ||
                     getLineItemValue(bsData, 'Total Current Liabilities') || 0,
      longTermDebt: getLineItemValue(bsData, 'Long-term Debt') || 
                    getLineItemValue(bsData, 'Total Long-term Debt') ||
                    getLineItemValue(bsData, 'Non-current Debt') ||
                    getLineItemValue(bsData, 'Total Non-current Liabilities') || 0,
      
      // P&L items (need annualization)
      totalRevenue: getAnnualizedValue(getLineItemValue(plData, 'Total Revenue')),
      netRevenue: getAnnualizedValue(getLineItemValue(plData, 'Net Revenue')),
      grossProfit: getAnnualizedValue(getLineItemValue(plData, 'Gross Margin')),
      operatingIncome: getAnnualizedValue(getLineItemValue(plData, 'Operating Income')),
      netIncome: getAnnualizedValue(getLineItemValue(plData, 'Net Income')),
      ebitda: getAnnualizedValue(FrontendRatiosCalculationService.calculateEBITDA(plData, bsData, period, periodIndex)),
      interestExpense: Math.abs(getAnnualizedValue(FrontendRatiosCalculationService.calculateInterestExpense(plData, period, periodIndex)) || 0),
      
      // Cash Flow items (flows, no annualization typically)
      operatingCashFlow: FrontendRatiosCalculationService.getOperatingCashFlow(cfData, period),
    };
  }
  
  // Helper methods for missing calculations  
  public static calculateEBITDA(plData: any, bsData: any, period: string, periodIndex: number): number | null {
    // First try to get EBITDA directly from P&L data (now that P&L service includes it)
    const directEBITDA = FrontendRatiosCalculationService.getLineItemValue(plData, 'EBITDA', periodIndex);
    if (directEBITDA !== null) {
      return directEBITDA;
    }
    
    // Fallback: If EBITDA not available in P&L data, return Net Income 
    // (the P&L service should handle EBITDA calculation now)
    const netIncome = FrontendRatiosCalculationService.getLineItemValue(plData, 'Net Income', periodIndex);
    if (netIncome === null) return null;
    
    console.warn('[Ratios Frontend] EBITDA not found in P&L data, falling back to Net Income. P&L service should calculate EBITDA.');
    return netIncome;
  }
  
  public static calculateInterestExpense(plData: any, period: string, periodIndex: number): number | null {
    // Look for interest expense in the P&L data - try various possible line item names
    const interestExpense = FrontendRatiosCalculationService.getLineItemValue(plData, 'Interest Expense', periodIndex) ||
                           FrontendRatiosCalculationService.getLineItemValue(plData, 'Total Interest Expense', periodIndex) ||
                           FrontendRatiosCalculationService.getLineItemValue(plData, 'Interest and Finance Costs', periodIndex) ||
                           FrontendRatiosCalculationService.getLineItemValue(plData, 'Finance Costs', periodIndex);
    
    if (interestExpense !== null) {
      return Math.abs(interestExpense); // Ensure positive value for expense
    }
    
    // If no specific interest expense found, return 0
    return 0;
  }
  
  public static getOperatingCashFlow(cfData: any, period: string): number | null {
    if (!cfData?.sections) return null;
    
    // Find the Operating Activities section
    const operatingSection = cfData.sections.find((section: any) => 
      section.key === 'net_cash_from_operating_activities'
    );
    
    if (!operatingSection?.line_items) return null;
    
    // Find the section total (Net Cash from Operating Activities)
    const sectionTotal = operatingSection.line_items.find((item: any) => 
      item.key === 'section_total' || item.name === 'Net Cash from Operating Activities'
    );
    
    if (!sectionTotal?.values) return null;
    
    const periodIndex = cfData.periods.indexOf(period);
    return periodIndex !== -1 ? sectionTotal.values[periodIndex] : null;
  }
  
  private static getLineItemValue(data: any, itemName: string, periodIndex?: number): number | null {
    if (!data?.line_items) return null;
    
    const lineItem = data.line_items.find((item: any) => item.name === itemName);
    if (!lineItem?.values) return null;
    
    if (periodIndex !== undefined) {
      return lineItem.values[periodIndex] ?? null;
    }
    
    return null;
  }
  
  private static getRatioDefinitions() {
    return [
      // Liquidity Ratios
      {
        name: 'Current Ratio',
        formula: 'Total Current Assets ÷ Total Current Liabilities',
        description: 'Measures the company\'s ability to pay short-term obligations.',
        account_mappings: ['Total Current Assets', 'Total Current Liabilities'],
        category: 'liquidity',
        format: 'decimal' as const,
        calc: (m: any) => m.currentLiabilities === 0 || m.currentLiabilities === null ? null : m.currentAssets / m.currentLiabilities
      },
      {
        name: 'Quick Ratio (Acid-Test)',
        formula: '(Cash + Short-Term Investments + Trade Accounts Receivable) ÷ Total Current Liabilities',
        description: 'Measures liquidity excluding inventory.',
        account_mappings: ['Cash', 'Short-Term Investments', 'Trade Accounts Receivable', 'Total Current Liabilities'],
        category: 'liquidity',
        format: 'decimal' as const,
        calc: (m: any) => {
          if (m.currentLiabilities === 0 || m.currentLiabilities === null) return null;
          const quickAssets = (m.cash || 0) + (m.shortTermInvestments || 0) + (m.accountsReceivable || 0);
          return quickAssets / m.currentLiabilities;
        }
      },
      {
        name: 'Cash Ratio',
        formula: 'Cash ÷ Total Current Liabilities',
        description: 'Measures the ability to pay short-term debts with cash.',
        account_mappings: ['Cash', 'Total Current Liabilities'],
        category: 'liquidity',
        format: 'decimal' as const,
        calc: (m: any) => m.currentLiabilities === 0 || m.currentLiabilities === null ? null : (m.cash || 0) / m.currentLiabilities
      },
      {
        name: 'Operating Cash Flow Ratio',
        formula: 'Operating Cash Flow ÷ Total Current Liabilities',
        description: 'Measures the ability to pay current liabilities with operating cash flow.',
        account_mappings: ['Operating Cash Flow', 'Total Current Liabilities'],
        category: 'liquidity',
        format: 'decimal' as const,
        calc: (m: any) => (m.currentLiabilities === 0 || m.currentLiabilities === null || !m.operatingCashFlow) ? null : m.operatingCashFlow / m.currentLiabilities
      },
      
      // Profitability Ratios
      {
        name: 'Gross Profit Margin',
        formula: 'Gross Profit ÷ Net Revenue',
        description: 'Measures gross profitability.',
        account_mappings: ['Gross Profit', 'Net Revenue'],
        category: 'profitability',
        format: 'percentage' as const,
        calc: (m: any) => m.netRevenue === 0 || m.netRevenue === null ? null : (m.grossProfit || 0) / m.netRevenue
      },
      {
        name: 'Operating Margin',
        formula: 'Operating Income ÷ Net Revenue',
        description: 'Measures operating profitability.',
        account_mappings: ['Operating Income', 'Net Revenue'],
        category: 'profitability',
        format: 'percentage' as const,
        calc: (m: any) => m.netRevenue === 0 || m.netRevenue === null ? null : (m.operatingIncome || 0) / m.netRevenue
      },
      {
        name: 'EBITDA Margin',
        formula: 'EBITDA ÷ Net Revenue',
        description: 'Measures cash profitability before interest, taxes, depreciation, and amortization.',
        account_mappings: ['EBITDA', 'Net Revenue'],
        category: 'profitability',
        format: 'percentage' as const,
        calc: (m: any) => m.netRevenue === 0 || m.netRevenue === null ? null : (m.ebitda || 0) / m.netRevenue
      },
      {
        name: 'Net Profit Margin',
        formula: 'Net Income ÷ Net Revenue',
        description: 'Measures overall profitability.',
        account_mappings: ['Net Income', 'Net Revenue'],
        category: 'profitability',
        format: 'percentage' as const,
        calc: (m: any) => m.netRevenue === 0 || m.netRevenue === null ? null : (m.netIncome || 0) / m.netRevenue
      },
      {
        name: 'Return on Assets (ROA)',
        formula: 'Net Income ÷ Total Assets',
        description: 'Measures profitability relative to total assets.',
        account_mappings: ['Net Income', 'Total Assets'],
        category: 'profitability',
        format: 'percentage' as const,
        calc: (m: any) => m.totalAssets === 0 || m.totalAssets === null ? null : (m.netIncome || 0) / m.totalAssets
      },
      {
        name: 'Return on Equity (ROE)',
        formula: 'Net Income ÷ Total Equity',
        description: 'Measures profitability relative to shareholders\' equity.',
        account_mappings: ['Net Income', 'Total Equity'],
        category: 'profitability',
        format: 'percentage' as const,
        calc: (m: any) => m.totalEquity === 0 || m.totalEquity === null ? null : (m.netIncome || 0) / m.totalEquity
      },
      
      // Leverage Ratios
      {
        name: 'Debt-to-Capital',
        formula: '(Short-term Debt + Long-term Debt) ÷ (Short-term Debt + Long-term Debt + Total Equity)',
        description: 'Measures debt as a percentage of total capital.',
        account_mappings: ['Short-term Debt', 'Long-term Debt', 'Total Equity'],
        category: 'leverage',
        format: 'percentage' as const,
        calc: (m: any) => {
          const totalDebt = (m.shortTermDebt || 0) + (m.longTermDebt || 0);
          const totalCapital = totalDebt + (m.totalEquity || 0);
          return totalCapital === 0 ? null : totalDebt / totalCapital;
        }
      },
      {
        name: 'Net Debt-to-Capital',
        formula: '(Short-term Debt + Long-term Debt - Cash) ÷ (Short-term Debt + Long-term Debt + Total Equity)',
        description: 'Measures net debt as a percentage of total capital.',
        account_mappings: ['Short-term Debt', 'Long-term Debt', 'Cash', 'Total Equity'],
        category: 'leverage',
        format: 'percentage' as const,
        calc: (m: any) => {
          const grossDebt = (m.shortTermDebt || 0) + (m.longTermDebt || 0);
          const netDebt = grossDebt - (m.cash || 0);
          const totalCapital = grossDebt + (m.totalEquity || 0);
          return totalCapital === 0 ? null : netDebt / totalCapital;
        }
      },
      {
        name: 'Debt-to-Equity',
        formula: '(Short-term Debt + Long-term Debt) ÷ Total Equity',
        description: 'Measures debt relative to equity.',
        account_mappings: ['Short-term Debt', 'Long-term Debt', 'Total Equity'],
        category: 'leverage',
        format: 'percentage' as const,
        calc: (m: any) => {
          if (m.totalEquity === 0 || m.totalEquity === null) return null;
          const totalDebt = (m.shortTermDebt || 0) + (m.longTermDebt || 0);
          return totalDebt / m.totalEquity;
        }
      },
      {
        name: 'Net Debt-to-Equity',
        formula: '(Short-term Debt + Long-term Debt - Cash) ÷ Total Equity',
        description: 'Measures net debt relative to equity.',
        account_mappings: ['Short-term Debt', 'Long-term Debt', 'Cash', 'Total Equity'],
        category: 'leverage',
        format: 'percentage' as const,
        calc: (m: any) => {
          if (m.totalEquity === 0 || m.totalEquity === null) return null;
          const grossDebt = (m.shortTermDebt || 0) + (m.longTermDebt || 0);
          const netDebt = grossDebt - (m.cash || 0);
          return netDebt / m.totalEquity;
        }
      },
      {
        name: 'Leverage Ratio',
        formula: '(Short-term Debt + Long-term Debt) ÷ EBITDA',
        description: 'Measures debt relative to earnings.',
        account_mappings: ['Short-term Debt', 'Long-term Debt', 'EBITDA'],
        category: 'leverage',
        format: 'multiple' as const,
        calc: (m: any) => {
          if (m.ebitda === 0 || m.ebitda === null) return null;
          const totalDebt = (m.shortTermDebt || 0) + (m.longTermDebt || 0);
          return totalDebt / m.ebitda;
        }
      },
      {
        name: 'Net Leverage Ratio',
        formula: '(Short-term Debt + Long-term Debt - Cash) ÷ EBITDA',
        description: 'Measures net debt relative to earnings.',
        account_mappings: ['Short-term Debt', 'Long-term Debt', 'Cash', 'EBITDA'],
        category: 'leverage',
        format: 'multiple' as const,
        calc: (m: any) => {
          if (m.ebitda === 0 || m.ebitda === null) return null;
          const grossDebt = (m.shortTermDebt || 0) + (m.longTermDebt || 0);
          const netDebt = grossDebt - (m.cash || 0);
          return netDebt / m.ebitda;
        }
      },
      {
        name: 'Interest Coverage Ratio',
        formula: 'EBITDA ÷ Interest Expense',
        description: 'Measures ability to pay interest expenses.',
        account_mappings: ['EBITDA', 'Interest Expense'],
        category: 'leverage',
        format: 'multiple' as const,
        calc: (m: any) => (m.interestExpense === 0 || m.interestExpense === null || m.ebitda === null) ? null : m.ebitda / m.interestExpense
      }
    ];
  }
  
  private static getRatioSections() {
    return [
      { id: 'liquidity', name: 'Liquidity Ratios' },
      { id: 'profitability', name: 'Profitability Ratios' },
      { id: 'leverage', name: 'Leverage Ratios' },
      { id: 'cash_conversion', name: 'Cash Conversion Cycle' }
    ];
  }
}
